import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBasketOverseasAddressCard from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketOverseasAddressCard";
import { SMARTSTORE_OVERSEAS_RELEASE_GUIDE_URL } from "@/features/pathpick/features/basket/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const STEP_KEYS = ["step_1", "step_2", "step_3", "step_4"];

// 3단계("해외 출고지 주소를 영문으로 입력") 바로 아래에 옮겨 적을 주소 카드를 끼워 넣는다.
const ADDRESS_CARD_AFTER_STEP = "step_3";

// 스마트스토어에 해외 출고지를 등록하는 방법을 알려주는 안내 모달
function PathPickBasketOverseasReleaseGuideModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();

  const handleOpenOfficialGuide = () => {
    window.open(
      SMARTSTORE_OVERSEAS_RELEASE_GUIDE_URL,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(720px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:basket.overseas_release_guide.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
            {t("path_pick:basket.overseas_release_guide.heading")}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t("path_pick:basket.overseas_release_guide.description")}
          </Text>
          <Flex
            flexDirection={"column"}
            gap={"6px"}
            padding={"12px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            background={"#F7F9FA"}
          >
            {STEP_KEYS.map((key, index) => (
              <React.Fragment key={key}>
                <Flex gap={"6px"}>
                  <Text textStyle={"body-sm-bold"} color={"#444440"}>
                    {`${index + 1}.`}
                  </Text>
                  <Text textStyle={"body-sm-medium"} color={"#444440"}>
                    {t(`path_pick:basket.overseas_release_guide.${key}`)}
                  </Text>
                </Flex>
                {key === ADDRESS_CARD_AFTER_STEP && (
                  <PathPickBasketOverseasAddressCard />
                )}
              </React.Fragment>
            ))}
          </Flex>
          <Flex
            flexDirection={"column"}
            gap={"4px"}
            padding={"12px"}
            borderRadius={"8px"}
            border={"0.5px solid #F5C79B"}
            background={"#FFF7ED"}
          >
            <Text textStyle={"body-sm-bold"} color={"#B27700"}>
              {t("path_pick:basket.overseas_release_guide.permission_title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#B27700"}>
              {t("path_pick:basket.overseas_release_guide.permission_note")}
            </Text>
          </Flex>
          <Text
            textStyle={"body-sm-bold"}
            color={"#1272EF"}
            width={"fit-content"}
            cursor={"pointer"}
            textDecoration={"underline"}
            onClick={handleOpenOfficialGuide}
          >
            {t("path_pick:basket.overseas_release_guide.official_link")}
          </Text>
          {/* 확인 버튼만 필요해서 공용 Footer(취소 버튼 고정 노출) 대신 직접 렌더한다. */}
          <Flex justifyContent={"flex-end"}>
            <AppButton variant={"primary"} width={"80px"} onClick={onClose}>
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.overseas_release_guide.confirm")}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickBasketOverseasReleaseGuideModal;
